import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, type UseMutationResult } from "@tanstack/react-query";
import { apiRequest, apiRequestSSE, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import {
  useLatestAnalysis,
  latestAnalysisQueryKey,
  type AnalysisFreshness,
} from "@/hooks/useLatestAnalysis";
import LastAnalysisStamp from "./last-analysis-stamp";
import { canAccessGoogleAdsIntegration } from "@shared/google-ads-access";
import { Button } from "./ui/button";
import {
  Sparkles,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Loader2,
  X,
  RefreshCw,
  BarChart3,
  Zap,
  ThumbsUp,
  ThumbsDown,
  Send,
  CheckCircle,
  Play,
  Pause,
  DollarSign,
  AlertTriangle,
  Settings2,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { motion, AnimatePresence } from "framer-motion";

/** Mutacja oceny rekomendacji — przekazywana w dol do kart z sekcjami. */
type FeedbackMutation = UseMutationResult<
  unknown,
  Error,
  { rating: number; recommendationSnippet: string; userCorrection?: string; variant?: string }
>;

/** Odpowiedz /api/marketing-analyst/analyze. */
type AnalysisResponse = {
  recommendations?: unknown;
  entities?: unknown;
  meta?: {
    hasMetaData?: boolean;
    hasGhlData?: boolean;
    hasGoogleAdsData?: boolean;
    tokensRemaining?: number;
    tokensDeducted?: number;
  };
};

/** Fragment cache /api/auth/me aktualizowany po odjeciu tokenow. */
type AuthMeCache = {
  user?: { tokensRemaining?: number; tokensUsedThisMonth?: number; [field: string]: unknown };
  [field: string]: unknown;
};

// --- Types ---
type AnalysisSection = {
  title: string;
  content: string;
};

type ApplyAction = {
  action: "update_budget" | "update_status";
  entityType: "campaign" | "adset" | "ad";
  entityId: string;
  entityName: string;
  newDailyBudget?: number;
  newLifetimeBudget?: number;
  newStatus?: "ACTIVE" | "PAUSED";
  description: string;
};

type MetaEntity = {
  id: string;
  name: string;
  type: "campaign" | "adset" | "ad";
  campaignId?: string;
  adSetId?: string;
  dailyBudget?: number | null;
  lifetimeBudget?: number | null;
  status?: string;
};

// --- Parser: splits AI markdown into sections by --- or ## headings ---
function splitIntoSections(raw: string): AnalysisSection[] {
  // Split by horizontal rules (---) first
  const hrChunks = raw.split(/\n---+\n/);
  const sections: AnalysisSection[] = [];

  for (const chunk of hrChunks) {
    // Within each HR chunk, split by ## or ### headings
    const lines = chunk.split(/\r?\n/);
    let currentTitle = "";
    let currentContent = "";

    for (const line of lines) {
      const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
      if (headingMatch) {
        // Save previous section if it has content
        if (currentContent.trim()) {
          sections.push({ title: currentTitle, content: currentContent.trim() });
        }
        currentTitle = headingMatch[2].replace(/\*+/g, "").trim();
        currentContent = "";
      } else {
        currentContent += (currentContent ? "\n" : "") + line;
      }
    }
    // Save last section in this chunk
    if (currentContent.trim()) {
      sections.push({ title: currentTitle, content: currentContent.trim() });
    }
  }

  // Merge sections that are too small (< 20 chars) with the next one
  const merged: AnalysisSection[] = [];
  for (let i = 0; i < sections.length; i++) {
    const s = sections[i];
    if (s.content.length < 20 && i + 1 < sections.length) {
      sections[i + 1].title = s.title || sections[i + 1].title;
      sections[i + 1].content = (s.content ? s.content + "\n\n" : "") + sections[i + 1].content;
    } else {
      merged.push(s);
    }
  }

  return merged.length > 0 ? merged : [{ title: "", content: raw }];
}

// --- Parser: extract APPLY action blocks from section content ---
function extractActions(content: string): { cleanContent: string; actions: ApplyAction[] } {
  const actions: ApplyAction[] = [];
  const seen = new Set<string>();
  const cleanContent = content
    .replace(/<!--\s*APPLY:(.*?)-->/gs, (_, jsonStr) => {
      try {
        const parsed = JSON.parse(jsonStr.trim());
        if (parsed.action && parsed.entityType && parsed.entityId && parsed.description) {
          // Deduplicate by entityId + action
          const key = `${parsed.entityId}:${parsed.action}:${parsed.newDailyBudget ?? ""}:${parsed.newStatus ?? ""}`;
          if (!seen.has(key)) {
            seen.add(key);
            actions.push(parsed as ApplyAction);
          }
        }
      } catch {
        // Ignore malformed action blocks
      }
      return "";
    })
    .trim();
  return { cleanContent, actions };
}

// --- Match entities from section content by name ---
function matchEntitiesInSection(
  content: string,
  title: string,
  entities: MetaEntity[]
): MetaEntity[] {
  if (entities.length === 0) return [];
  const fullText = (title + " " + content).toLowerCase();
  const matched: MetaEntity[] = [];
  const seen = new Set<string>();
  // Only match adsets (ads will be shown as children of matched adsets)
  for (const entity of entities) {
    if (entity.type !== "adset") continue;
    if (seen.has(entity.id)) continue;
    const nameLower = entity.name.toLowerCase();
    if (nameLower.length >= 5 && fullText.includes(nameLower)) {
      seen.add(entity.id);
      matched.push(entity);
    }
  }
  return matched;
}

// --- Simple status toggle for a single ad ---
function AdStatusToggle({
  adId,
  adName,
  currentStatus,
  forUserId,
}: {
  adId: string;
  adName: string;
  currentStatus: string;
  forUserId?: string;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const { t } = useTranslation("recommendationsPanel");
  const isActive = currentStatus === "ACTIVE";
  const newStatus = isActive ? "PAUSED" : "ACTIVE";

  const mutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", "/api/marketing-analyst/apply-action", {
        action: "update_status",
        entityType: "ad",
        entityId: adId,
        entityName: adName,
        newStatus,
        description:
          newStatus === "PAUSED"
            ? t("adToggle.disableAd", { name: adName })
            : t("adToggle.enableAd", { name: adName }),
        forUserId,
      }),
    onSuccess: () => setConfirmed(false),
  });

  if (mutation.isSuccess) {
    return (
      <span className="text-[10px] text-emerald-400 flex items-center gap-1">
        <CheckCircle className="w-3 h-3" />
        {newStatus === "PAUSED" ? t("adToggle.disabled") : t("adToggle.enabled")}
      </span>
    );
  }

  if (confirmed) {
    return (
      <span className="inline-flex items-center gap-1">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 disabled:opacity-50"
        >
          {mutation.isPending ? (
            <Loader2 className="w-2.5 h-2.5 animate-spin" />
          ) : (
            t("adToggle.yes")
          )}
        </button>
        <button onClick={() => setConfirmed(false)} className="text-[10px] text-white/40">
          {t("adToggle.no")}
        </button>
        {mutation.isError && (
          <span className="text-[10px] text-red-400">{(mutation.error as Error).message}</span>
        )}
      </span>
    );
  }

  return (
    <button
      onClick={() => setConfirmed(true)}
      className={`px-1.5 py-0.5 rounded text-[10px] border transition-colors ${
        isActive
          ? "border-orange-400/50 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-orange-600/40 dark:bg-orange-950/30 dark:text-orange-300 dark:hover:bg-orange-950/50"
          : "border-green-400/50 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-600/40 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50"
      }`}
    >
      {isActive ? t("adToggle.disable") : t("adToggle.enable")}
    </button>
  );
}

// --- Inline entity action buttons (budget + status) ---
function EntityInlineActions({
  entity,
  forUserId,
  allEntities,
}: {
  entity: MetaEntity;
  forUserId?: string;
  allEntities: MetaEntity[];
}) {
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetValue, setBudgetValue] = useState(
    (entity.dailyBudget ?? entity.lifetimeBudget ?? "").toString()
  );
  const [budgetConfirmed, setBudgetConfirmed] = useState(false);
  const [statusConfirmed, setStatusConfirmed] = useState(false);
  const [showAds, setShowAds] = useState(false);

  const { t } = useTranslation("recommendationsPanel");

  const isLifetime = entity.lifetimeBudget != null && entity.dailyBudget == null;
  const isActive = entity.status === "ACTIVE";
  const newStatus = isActive ? "PAUSED" : "ACTIVE";
  const hasBudget = entity.type !== "ad";

  // Find child ads for this ad set
  const childAds =
    entity.type === "adset"
      ? allEntities.filter((e) => e.type === "ad" && e.adSetId === entity.id)
      : []; // ads don't have budgets

  const budgetMutation = useMutation({
    mutationFn: async () => {
      const numVal = parseFloat(budgetValue);
      if (isNaN(numVal) || numVal <= 0) throw new Error(t("entityActions.invalidAmount"));
      return apiRequest("POST", "/api/marketing-analyst/apply-action", {
        action: "update_budget",
        entityType: entity.type,
        entityId: entity.id,
        entityName: entity.name,
        ...(isLifetime ? { newLifetimeBudget: numVal } : { newDailyBudget: numVal }),
        description: t("entityActions.changeBudgetDesc", { amount: numVal }),
        forUserId,
      });
    },
    onSuccess: () => {
      setEditingBudget(false);
      setBudgetConfirmed(false);
    },
  });

  const statusMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/marketing-analyst/apply-action", {
        action: "update_status",
        entityType: entity.type,
        entityId: entity.id,
        entityName: entity.name,
        newStatus,
        description:
          newStatus === "PAUSED"
            ? t("entityActions.stopDesc", { name: entity.name })
            : t("entityActions.enableDesc", { name: entity.name }),
        forUserId,
      });
    },
    onSuccess: () => setStatusConfirmed(false),
  });

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1">
      {/* Budget button */}
      {hasBudget && !editingBudget && !budgetMutation.isSuccess && (
        <button
          onClick={() => setEditingBudget(true)}
          className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-blue-400/50 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-600/40 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50 transition-colors"
        >
          <DollarSign className="w-3 h-3" />
          {t("entityActions.budget")}
        </button>
      )}

      {/* Status button */}
      {!statusMutation.isSuccess && !statusConfirmed && (
        <button
          onClick={() => setStatusConfirmed(true)}
          className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] border transition-colors ${
            isActive
              ? "border-orange-400/50 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-orange-600/40 dark:bg-orange-950/30 dark:text-orange-300 dark:hover:bg-orange-950/50"
              : "border-green-400/50 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-600/40 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50"
          }`}
        >
          {isActive ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 translate-x-[8%]" />}
          {isActive ? t("entityActions.stop") : t("entityActions.enable")}
        </button>
      )}

      {/* Status confirmation */}
      {statusConfirmed && !statusMutation.isSuccess && (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-white/[0.1] bg-white/[0.03]">
          <span className="text-[11px] text-white/70">
            {newStatus === "PAUSED" ? t("entityActions.stopQ") : t("entityActions.enableQ")}?
          </span>
          <button
            onClick={() => statusMutation.mutate()}
            disabled={statusMutation.isPending}
            className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-emerald-600/30 text-emerald-300 hover:bg-emerald-600/50 border border-emerald-500/40 disabled:opacity-50"
          >
            {statusMutation.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              t("entityActions.yes")
            )}
          </button>
          <button
            onClick={() => setStatusConfirmed(false)}
            className="text-[11px] text-white/50 hover:text-white/70"
          >
            {t("entityActions.no")}
          </button>
        </div>
      )}

      {/* Status success */}
      {statusMutation.isSuccess && (
        <span className="flex items-center gap-1 text-[11px] text-emerald-400">
          <CheckCircle className="w-3 h-3" />{" "}
          {newStatus === "PAUSED" ? t("entityActions.stopped") : t("entityActions.enabled")}
        </span>
      )}
      {statusMutation.isError && (
        <span className="text-[11px] text-red-400">{(statusMutation.error as Error).message}</span>
      )}

      {/* Budget editor */}
      {editingBudget && !budgetMutation.isSuccess && (
        <div className="flex items-center gap-1.5 w-full mt-1">
          {!budgetConfirmed ? (
            <>
              <input
                type="number"
                step="1"
                min="1"
                value={budgetValue}
                onChange={(e) => setBudgetValue(e.target.value)}
                className="w-20 px-2 py-1 text-[11px] bg-[#0a0c10] border border-white/[0.15] rounded text-white focus:outline-none focus:border-[#ef3e29]/50"
                placeholder={t("entityActions.amountPlaceholder")}
              />
              <button
                onClick={() => setBudgetConfirmed(true)}
                disabled={!budgetValue || parseFloat(budgetValue) <= 0}
                className="px-2 py-1 text-[11px] rounded bg-[#ef3e29]/20 text-[#ef3e29] hover:bg-[#ef3e29]/30 border border-[#ef3e29]/30 disabled:opacity-40"
              >
                {t("entityActions.change")}
              </button>
              <button
                onClick={() => setEditingBudget(false)}
                className="text-[11px] text-white/50 hover:text-white/70"
              >
                ✕
              </button>
            </>
          ) : (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-white/[0.1] bg-white/[0.03]">
              <span className="text-[11px] text-white/70">
                {t("entityActions.changeTo")} <strong>{budgetValue} zł</strong>
                {isLifetime ? t("entityActions.lifetime") : t("entityActions.perDay")}?
              </span>
              <button
                onClick={() => budgetMutation.mutate()}
                disabled={budgetMutation.isPending}
                className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-emerald-600/30 text-emerald-300 hover:bg-emerald-600/50 border border-emerald-500/40 disabled:opacity-50"
              >
                {budgetMutation.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  t("entityActions.yes")
                )}
              </button>
              <button
                onClick={() => setBudgetConfirmed(false)}
                className="text-[11px] text-white/50 hover:text-white/70"
              >
                {t("entityActions.no")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Budget success */}
      {budgetMutation.isSuccess && (
        <span className="flex items-center gap-1 text-[11px] text-emerald-400">
          <CheckCircle className="w-3 h-3" />{" "}
          {t("entityActions.budgetChanged", { amount: budgetValue })}
        </span>
      )}
      {budgetMutation.isError && (
        <span className="text-[11px] text-red-400">{(budgetMutation.error as Error).message}</span>
      )}

      {/* Child ads list */}
      {childAds.length > 0 && (
        <div className="w-full mt-1">
          <button
            onClick={() => setShowAds(!showAds)}
            className="flex items-center gap-1 text-[10px] text-white/40 hover:text-white/60 transition-colors"
          >
            {showAds ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {t("entityActions.ads", { count: childAds.length })}
          </button>
          {showAds && (
            <div className="mt-1 ml-2 pl-2 border-l border-white/[0.06] space-y-1">
              {childAds.map((ad) => (
                <div key={ad.id} className="flex items-center justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-white/60 truncate max-w-[200px]">
                    {ad.name}
                  </span>
                  <AdStatusToggle
                    adId={ad.id}
                    adName={ad.name}
                    currentStatus={ad.status || "ACTIVE"}
                    forUserId={forUserId}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Action button component ---
function ActionButton({
  action,
  forUserId,
  onApplied,
}: {
  action: ApplyAction;
  forUserId?: string;
  onApplied?: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [applied, setApplied] = useState(false);

  const { t } = useTranslation("recommendationsPanel");

  const applyMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/marketing-analyst/apply-action", {
        ...action,
        forUserId,
      });
    },
    onSuccess: () => {
      setApplied(true);
      setConfirmed(false);
      onApplied?.();
    },
  });

  if (applied) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-950/40 border border-emerald-700/30 text-emerald-300 text-xs">
        <CheckCircle className="w-3.5 h-3.5" />
        <span>{t("actionButton.applied", { description: action.description })}</span>
      </div>
    );
  }

  if (applyMutation.isError) {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-950/40 border border-red-700/30 text-red-300 text-xs">
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>
            {t("actionButton.errorPrefix")}{" "}
            {(applyMutation.error as Error | null)?.message || t("actionButton.applyError")}
          </span>
        </div>
        <button
          onClick={() => {
            applyMutation.reset();
            setConfirmed(false);
          }}
          className="text-xs text-white/50 hover:text-white/70 transition-colors"
        >
          {t("actionButton.retry")}
        </button>
      </div>
    );
  }

  const icon =
    action.action === "update_status" ? (
      action.newStatus === "PAUSED" ? (
        <Pause className="w-3.5 h-3.5" />
      ) : (
        <Play className="w-3.5 h-3.5" />
      )
    ) : (
      <DollarSign className="w-3.5 h-3.5" />
    );

  const colorClasses =
    action.action === "update_status" && action.newStatus === "PAUSED"
      ? "border-orange-400/50 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-orange-600/40 dark:bg-orange-950/30 dark:text-orange-300 dark:hover:bg-orange-950/50"
      : "border-blue-400/50 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-600/40 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50";

  if (!confirmed) {
    return (
      <button
        onClick={() => setConfirmed(true)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${colorClasses}`}
      >
        {icon}
        <span>{t("actionButton.apply", { description: action.description })}</span>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-muted/50">
      <AlertTriangle className="w-3.5 h-3.5 text-[#ef3e29] flex-shrink-0" />
      <span className="text-xs text-muted-foreground flex-1">
        {action.action === "update_status" && action.newStatus === "PAUSED"
          ? t("actionButton.stopQ", { name: action.entityName })
          : action.action === "update_budget"
            ? t("actionButton.changeBudgetQ", {
                name: action.entityName,
                budget: action.newDailyBudget,
              })
            : t("actionButton.applyQ", { name: action.entityName })}
      </span>
      <button
        onClick={() => applyMutation.mutate()}
        disabled={applyMutation.isPending}
        className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-emerald-600/30 text-emerald-300 hover:bg-emerald-600/50 border border-emerald-500/40 transition-colors disabled:opacity-50"
      >
        {applyMutation.isPending ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <CheckCircle className="w-3 h-3" />
        )}
        {t("actionButton.yes")}
      </button>
      <button
        onClick={() => setConfirmed(false)}
        className="px-2.5 py-1 rounded text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {t("actionButton.no")}
      </button>
    </div>
  );
}

// --- Section feedback component ---
function SectionFeedback({
  section,
  index,
  variant,
  isAdmin,
  feedbackMutation,
  forUserId,
  entities,
}: {
  section: AnalysisSection;
  index: number;
  variant: string;
  isAdmin: boolean;
  feedbackMutation: FeedbackMutation;
  forUserId?: string;
  entities: MetaEntity[];
}) {
  const [feedbackMode, setFeedbackMode] = useState<"idle" | "positive" | "correction">("idle");
  const [feedbackSent, setFeedbackSent] = useState<"up" | "down" | null>(null);
  const [positiveNote, setPositiveNote] = useState("");
  const [correctionText, setCorrectionText] = useState("");

  const { t } = useTranslation("recommendationsPanel");

  // Extract action blocks from section content
  const { cleanContent, actions } = extractActions(section.content);

  // Match entities mentioned in this section
  const matchedEntities = matchEntitiesInSection(cleanContent, section.title, entities);

  const handleFeedback = (rating: number) => {
    feedbackMutation.mutate({
      rating,
      recommendationSnippet:
        (section.title ? `[${section.title}] ` : "") + cleanContent.slice(0, 500),
      userCorrection: rating === 1 ? positiveNote || undefined : correctionText || undefined,
      variant,
    });
    setFeedbackSent(rating === 1 ? "up" : "down");
    setFeedbackMode("idle");
    setPositiveNote("");
    setCorrectionText("");
    setTimeout(() => setFeedbackSent(null), 3000);
  };

  return (
    <div className="border border-border rounded-xl bg-muted/30 p-3">
      {section.title && (
        <div className="font-semibold text-foreground text-sm mb-1.5">{section.title}</div>
      )}
      <div
        className="text-foreground text-[13px] leading-7 tracking-wide
        [&_strong]:text-foreground [&_strong]:font-bold
        [&_em]:text-foreground/80
        [&_ul]:my-1 [&_ul]:ml-1 [&_ol]:my-1 [&_ol]:ml-1
        [&_li]:my-1 [&_li]:pl-1
        [&_p]:my-1.5
        [&_code]:text-[#ef3e29] [&_code]:bg-[#ef3e29]/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded
        [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-foreground [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:border-b [&_h2]:border-border [&_h2]:pb-1
        [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-foreground/90 [&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:pl-2 [&_h3]:border-l-2 [&_h3]:border-[#ef3e29]/40
        [&_h4]:text-xs [&_h4]:font-medium [&_h4]:text-muted-foreground [&_h4]:mt-2 [&_h4]:mb-0.5 [&_h4]:pl-4 [&_h4]:uppercase [&_h4]:tracking-wider
        [&_blockquote]:ml-4 [&_blockquote]:pl-3 [&_blockquote]:py-1.5 [&_blockquote]:my-1.5 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:bg-muted/40 [&_blockquote]:rounded-r [&_blockquote]:text-muted-foreground [&_blockquote]:text-xs [&_blockquote]:leading-6 [&_blockquote_strong]:text-foreground/80 [&_blockquote_strong]:font-semibold
        [&_hr]:border-border [&_hr]:my-3
      "
      >
        <ReactMarkdown>{cleanContent}</ReactMarkdown>
      </div>

      {/* Apply action buttons (from AI APPLY blocks) */}
      {actions.length > 0 && (
        <div className="mt-2 space-y-2">
          {actions.map((action, ai) => (
            <ActionButton key={`action-${index}-${ai}`} action={action} forUserId={forUserId} />
          ))}
        </div>
      )}

      {/* Inline entity action buttons (budget/status for each matched entity) */}
      {matchedEntities.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border space-y-2">
          {matchedEntities.map((entity) => (
            <div key={entity.id} className="flex items-start gap-2">
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground min-w-[60px] pt-1.5">
                <Settings2 className="w-3 h-3" />
                <span className="truncate max-w-[120px]">{entity.name}</span>
              </div>
              <div className="flex-1">
                <EntityInlineActions entity={entity} forUserId={forUserId} allEntities={entities} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Feedback UI (admin only) */}
      {isAdmin && (
        <div className="mt-2 pt-2 border-t border-border">
          {feedbackSent && (
            <div className="flex items-center gap-2 text-xs text-emerald-400">
              <CheckCircle className="w-3.5 h-3.5" />
              <span>{t("sectionFeedback.saved")}</span>
            </div>
          )}
          {!feedbackSent && feedbackMode === "idle" && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFeedbackMode("positive")}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs text-emerald-400 hover:bg-emerald-900/30 border border-emerald-700/30 hover:border-emerald-600/50 transition-colors"
              >
                <ThumbsUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => setFeedbackMode("correction")}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs text-red-400 hover:bg-red-900/30 border border-red-700/30 hover:border-red-600/50 transition-colors"
              >
                <ThumbsDown className="w-3 h-3" />
              </button>
            </div>
          )}
          {!feedbackSent && feedbackMode === "positive" && (
            <div className="space-y-2">
              <textarea
                value={positiveNote}
                onChange={(e) => setPositiveNote(e.target.value)}
                placeholder={t("sectionFeedback.positivePlaceholder")}
                className="w-full bg-muted border border-border rounded px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:border-emerald-600/50"
                rows={2}
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => {
                    setFeedbackMode("idle");
                    setPositiveNote("");
                  }}
                  className="px-2.5 py-1 rounded text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("sectionFeedback.cancel")}
                </button>
                <button
                  onClick={() => handleFeedback(1)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60 border border-emerald-700/40 transition-colors"
                >
                  <Send className="w-3 h-3" /> {t("sectionFeedback.send")}
                </button>
              </div>
            </div>
          )}
          {!feedbackSent && feedbackMode === "correction" && (
            <div className="space-y-2">
              <textarea
                value={correctionText}
                onChange={(e) => setCorrectionText(e.target.value)}
                placeholder={t("sectionFeedback.negativePlaceholder")}
                className="w-full bg-muted border border-border rounded px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:border-red-600/50"
                rows={2}
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => {
                    setFeedbackMode("idle");
                    setCorrectionText("");
                  }}
                  className="px-2.5 py-1 rounded text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("sectionFeedback.cancel")}
                </button>
                <button
                  onClick={() => handleFeedback(0)}
                  disabled={!correctionText.trim()}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs bg-red-900/40 text-red-300 hover:bg-red-900/60 border border-red-700/40 disabled:opacity-40 transition-colors"
                >
                  <Send className="w-3 h-3" /> {t("sectionFeedback.send")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Main component ---
interface RecommendationsPanelProps {
  variant: string;
  selectedModel: string;
  forUserId?: string;
  clientName?: string;
  clientAdAccountIds?: string[];
  onAnalysisComplete?: (text: string) => void;
}

export default function RecommendationsPanel({
  variant,
  selectedModel,
  forUserId,
  clientName,
  clientAdAccountIds,
  onAnalysisComplete,
}: RecommendationsPanelProps) {
  const [recommendations, setRecommendations] = useState<string | null>(null);
  const [parsed, setParsed] = useState<AnalysisSection[] | null>(null);
  const [entities, setEntities] = useState<MetaEntity[]>([]);
  const [expanded, setExpanded] = useState(true);
  const [dataSources, setDataSources] = useState<{
    meta: boolean;
    ghl: boolean;
    googleAds: boolean;
  }>({ meta: false, ghl: false, googleAds: false });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [platformFilter, setPlatformFilter] = useState<"all" | "meta" | "google">("all");
  // VP-256: skąd pochodzi to, co widać — z cache (z datą) czy ze świeżego przeliczenia.
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [freshness, setFreshness] = useState<AnalysisFreshness | null>(null);
  const analysisMode = "short" as const;

  const { user } = useAuth();
  const { t } = useTranslation("recommendationsPanel");
  const isAdmin = user?.role === "agency_owner" || user?.role === "super_admin";

  const feedbackMutation = useMutation({
    mutationFn: async (params: {
      rating: number;
      recommendationSnippet: string;
      userCorrection?: string;
    }) => {
      return apiRequest("POST", "/api/marketing-analyst/feedback", {
        ...params,
        variant,
      });
    },
  });

  // Check integration statuses (supports forUserId for client analysis)
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";
  const { data: metaStatus } = useQuery<{ connected: boolean; selectedAdAccountId?: string }>({
    queryKey: ["meta-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/meta/status${fuParam}`),
    staleTime: 30_000,
  });
  const { data: ghlStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["ghl-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/crm/status${fuParam}`),
    staleTime: 30_000,
  });
  // VP-192: Google Ads jest ukryte w UI (brak weryfikacji Google OAuth), więc dla
  // zwykłego usera nie pytamy o status — filtr platformy i tak pokazuje się tylko
  // przy dwóch podłączonych platformach.
  const canSeeGoogleAds = canAccessGoogleAdsIntegration(user);
  const { data: googleAdsStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["google-ads-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/google-ads/status${fuParam}`),
    staleTime: 30_000,
    enabled: canSeeGoogleAds,
  });

  const metaConnected = !!metaStatus?.connected;
  const ghlConnected = !!ghlStatus?.connected;
  const googleAdsConnected = !!googleAdsStatus?.connected;
  const hasAnyIntegration = metaConnected || ghlConnected || googleAdsConnected;

  // VP-256: ostatnia zapisana analiza (GET, zero tokenów). Zakres wchodzi do klucza,
  // bo analiza Meta i analiza Google to dwa różne wyniki.
  const analysisScope = { analysisMode, platformFilter };
  const { data: latestAnalysisData } = useLatestAnalysis(variant, forUserId, analysisScope);

  // Wstrzykujemy zapisany wynik tylko raz na (wariant, klient, zakres) — inaczej
  // zamknięcie panelu krzyżykiem natychmiast przywracałoby treść z cache.
  const hydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${variant}|${forUserId ?? ""}|${analysisMode}|${platformFilter}`;
    if (hydratedKeyRef.current === key) return;
    const cached = latestAnalysisData?.analysis;
    if (!cached) return;
    hydratedKeyRef.current = key;
    setRecommendations(cached.recommendations);
    setParsed(splitIntoSections(cached.recommendations));
    setCachedAt(cached.createdAt);
    setFreshness(cached.freshness);
    // Zapisany wynik startuje zwinięty — status widać po plakietce w nagłówku,
    // a treść nie zasłania czatu przy każdym wejściu.
    setExpanded(false);
  }, [latestAnalysisData, variant, forUserId, analysisMode, platformFilter]);

  const analysisMutation = useMutation({
    mutationFn: async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180_000);
      try {
        // SSE + heartbeats — the multi-timeframe Meta/GHL/Google fetch + LLM runs
        // long enough to trip CloudFront's origin timeout as plain JSON (VP-200).
        const result = await apiRequestSSE(
          "POST",
          "/api/marketing-analyst/quick-analysis",
          {
            variant,
            selectedModel,
            analysisMode,
            forUserId,
            clientName,
            clientAdAccountIds,
            platformFilter,
          },
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);
        return result;
      } catch (err) {
        clearTimeout(timeoutId);
        // apiRequestSSE rethrows an aborted/timed-out request as Error('REQUEST_TIMEOUT: …')
        // (name 'Error'), not a DOMException 'AbortError' — match both.
        if (
          (err as Error)?.name === "AbortError" ||
          (typeof (err as Error)?.message === "string" &&
            (err as Error).message.startsWith("REQUEST_TIMEOUT"))
        ) {
          throw new Error(t("panel.timeout"));
        }
        throw err;
      }
    },
    onSuccess: (data: AnalysisResponse) => {
      let text = typeof data.recommendations === "string" ? data.recommendations : "";
      if (typeof text === "string" && text.trim().startsWith("{")) {
        try {
          const p = JSON.parse(text) as Record<string, unknown>;
          const unwrapped = p.json || p.text || p.content || p.recommendations;
          if (typeof unwrapped === "string") text = unwrapped;
        } catch {}
      }
      setRecommendations(text);
      setParsed(splitIntoSections(text));
      setEntities(Array.isArray(data.entities) ? data.entities : []);
      // Świeże przeliczenie — plakietka pokazuje "teraz", a zapisany wynik w cache
      // jest już nieaktualny (backend właśnie dopisał nowy wpis).
      setCachedAt(new Date().toISOString());
      setFreshness("fresh");
      queryClient.invalidateQueries({
        queryKey: latestAnalysisQueryKey(variant, forUserId, analysisScope),
      });
      onAnalysisComplete?.(text);
      setDataSources({
        meta: data.meta?.hasMetaData || false,
        ghl: data.meta?.hasGhlData || false,
        googleAds: data.meta?.hasGoogleAdsData || false,
      });
      setExpanded(true);
      const responseMeta = data.meta;
      if (responseMeta?.tokensRemaining !== undefined) {
        queryClient.setQueryData(["/api/auth/me"], (prev: AuthMeCache | undefined) => {
          if (!prev?.user) return prev;
          return {
            ...prev,
            user: {
              ...prev.user,
              tokensRemaining: responseMeta.tokensRemaining,
              ...(typeof responseMeta.tokensDeducted === "number" &&
              typeof prev.user.tokensUsedThisMonth === "number"
                ? {
                    tokensUsedThisMonth:
                      prev.user.tokensUsedThisMonth + responseMeta.tokensDeducted,
                  }
                : {}),
            },
          };
        });
        queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      }
    },
    onError: (error) => {
      setErrorMessage(error?.message || t("panel.unknownError"));
    },
  });

  if (!hasAnyIntegration) return null;

  const bothPlatformsConnected = metaConnected && googleAdsConnected;

  return (
    <div className="space-y-3">
      {/* Platform filter (show only when both platforms connected) */}
      {bothPlatformsConnected && !recommendations && !analysisMutation.isPending && (
        <div className="flex items-center gap-1.5 px-1">
          {(["all", "meta", "google"] as const).map((option) => {
            const labels: Record<typeof option, string> = {
              all: t("panel.platforms.all"),
              meta: t("panel.platforms.meta"),
              google: t("panel.platforms.google"),
            };
            return (
              <button
                key={option}
                onClick={() => setPlatformFilter(option)}
                className={`text-[11px] px-2.5 py-1 rounded-lg border transition-all ${
                  platformFilter === option
                    ? "bg-white/[0.08] border-[#ef3e29]/40 text-white"
                    : "bg-white/[0.02] border-white/[0.06] text-white/40 hover:text-white/60 hover:border-white/[0.12]"
                }`}
              >
                {labels[option]}
              </button>
            );
          })}
        </div>
      )}

      {/* Trigger button */}
      {!recommendations && !analysisMutation.isPending && (
        <button
          onClick={() => analysisMutation.mutate()}
          className="w-full group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-[#ef3e29]/30 transition-all duration-300 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#ef3e29]/15 border border-[#ef3e29]/25">
                <Zap className="w-5 h-5 text-[#ef3e29]" />
              </div>
              <div className="text-left">
                <span className="text-sm font-semibold text-white block">
                  {t("panel.quickAnalysis")}
                </span>
                <span className="text-xs text-white/40">{t("panel.kpiCheck")}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {metaConnected && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-600/15 text-blue-400 border border-blue-500/20 font-medium">
                  META
                </span>
              )}
              {googleAdsConnected && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600/15 text-green-400 border border-green-500/20 font-medium">
                  GOOGLE
                </span>
              )}
              {ghlConnected && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-600/15 text-orange-400 border border-orange-500/20 font-medium">
                  CRM
                </span>
              )}
              <Sparkles className="w-4 h-4 text-[#ef3e29]/70 ml-1 group-hover:text-[#ef3e29] group-hover:scale-110 transition-all" />
            </div>
          </div>
        </button>
      )}

      {/* Loading state */}
      {analysisMutation.isPending && (
        <div className="p-4 bg-white/[0.02] border border-white/[0.08] rounded-xl">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-[#ef3e29] animate-spin flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-white">{t("panel.analyzing")}</p>
              <p className="text-xs text-white/40 mt-0.5">
                {[
                  metaConnected && "Meta Ads",
                  googleAdsConnected && "Google Ads",
                  ghlConnected && "LeadConnector",
                ]
                  .filter(Boolean)
                  .join(" + ")}
              </p>
            </div>
          </div>
          <div className="mt-3 w-full bg-white/[0.06] rounded-full h-1 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#ef3e29] to-[#ef3e29]/60 rounded-full animate-pulse"
              style={{ width: "60%" }}
            />
          </div>
        </div>
      )}

      {/* Error state */}
      {analysisMutation.isError && !recommendations && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
          <p className="text-sm text-red-300">
            {errorMessage?.includes("401") ||
            errorMessage?.includes("autoryzacji") ||
            errorMessage?.includes("zaloguj")
              ? t("panel.errors.session")
              : errorMessage?.includes("Insufficient tokens") ||
                  errorMessage?.includes("Brak tokenów")
                ? t("panel.errors.noTokens")
                : errorMessage?.includes("integracji")
                  ? t("panel.errors.noIntegrations")
                  : errorMessage?.includes("timeout") ||
                      errorMessage?.includes("Timeout") ||
                      errorMessage?.includes("network") ||
                      errorMessage?.includes("Failed to fetch") ||
                      errorMessage?.includes("AbortError")
                    ? t("panel.errors.timeout")
                    : errorMessage?.includes("Internal Server Error") ||
                        // VP-293: untyped 5xx arrive as "500: <localized SYS_001 text>"
                        errorMessage?.startsWith("500:") ||
                        errorMessage?.includes("503") ||
                        errorMessage?.includes("502") ||
                        errorMessage?.includes("AI service") ||
                        errorMessage?.includes("Key limit") ||
                        errorMessage?.includes("weekly limit")
                      ? t("panel.errors.aiUnavailable")
                      : t("panel.errors.generic", {
                          message: errorMessage || t("panel.unknownError"),
                        })}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setErrorMessage(null);
              analysisMutation.mutate();
            }}
            className="mt-2 text-red-300 hover:text-red-200"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            {t("panel.retry")}
          </Button>
        </div>
      )}

      {/* Recommendations display */}
      <AnimatePresence>
        {recommendations && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/[0.02] border border-white/[0.08] rounded-xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-white/[0.03] border-b border-white/[0.06]">
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-2 text-white hover:text-white/80 transition-colors"
              >
                <BarChart3 className="w-4 h-4 text-[#ef3e29]" />
                <span className="text-sm font-semibold">{t("panel.aiRecommendations")}</span>
                <div className="flex items-center gap-1 ml-1">
                  {dataSources.meta && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-blue-600/15 text-blue-400 border border-blue-500/20">
                      META
                    </span>
                  )}
                  {dataSources.googleAds && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-green-600/15 text-green-400 border border-green-500/20">
                      GOOGLE
                    </span>
                  )}
                  {dataSources.ghl && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-orange-600/15 text-orange-400 border border-orange-500/20">
                      CRM
                    </span>
                  )}
                </div>
                {expanded ? (
                  <ChevronUp className="w-3.5 h-3.5 text-white/40" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-white/40" />
                )}
              </button>
              <div className="flex items-center gap-1">
                {cachedAt && freshness && (
                  <LastAnalysisStamp createdAt={cachedAt} freshness={freshness} />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => analysisMutation.mutate()}
                  disabled={analysisMutation.isPending}
                  className="h-7 px-2 text-white/50 hover:text-white hover:bg-white/[0.06]"
                  title={t("panel.refreshCosts")}
                >
                  <RefreshCw
                    className={`w-3.5 h-3.5 ${analysisMutation.isPending ? "animate-spin" : ""}`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRecommendations(null);
                    setParsed(null);
                    setExpanded(true);
                  }}
                  className="h-7 px-2 text-white/40 hover:text-white/60 hover:bg-white/[0.04]"
                  title={t("panel.close")}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            {expanded && (
              <div className="px-4 py-3 overflow-y-auto custom-scrollbar">
                {parsed && parsed.length > 0 ? (
                  <div className="space-y-4">
                    {parsed.map((sec, i) => (
                      <SectionFeedback
                        key={(sec.title || "sec") + i}
                        section={sec}
                        index={i}
                        variant={variant}
                        isAdmin={isAdmin}
                        feedbackMutation={feedbackMutation}
                        forUserId={forUserId}
                        entities={entities}
                      />
                    ))}
                  </div>
                ) : (
                  <div
                    className="text-white/90 text-[13px] leading-7 tracking-wide
                    [&_h1]:text-lg [&_h1]:font-bold [&_h1]:text-white [&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:border-b [&_h1]:border-white/[0.08] [&_h1]:pb-2
                    [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:border-b [&_h2]:border-white/[0.06] [&_h2]:pb-2
                    [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-white/90 [&_h3]:mt-5 [&_h3]:mb-2
                    [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-white/80 [&_h4]:mt-4 [&_h4]:mb-2
                    [&_p]:my-2.5
                    [&_strong]:text-white [&_strong]:font-semibold
                    [&_em]:text-[#ef3e29]/80
                    [&_ul]:my-2 [&_ul]:ml-1 [&_ol]:my-2 [&_ol]:ml-1
                    [&_li]:my-1.5 [&_li]:pl-1
                    [&_a]:text-[#ef3e29] [&_a]:underline
                    [&_code]:text-[#ef3e29]/80 [&_code]:bg-white/[0.04] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded
                    [&_hr]:border-white/[0.08] [&_hr]:my-5
                    [&_blockquote]:border-l-2 [&_blockquote]:border-[#ef3e29]/30 [&_blockquote]:pl-3 [&_blockquote]:my-3 [&_blockquote]:text-white/70
                    [&_table]:w-full [&_table]:my-3 [&_table]:border-collapse [&_table]:text-xs
                    [&_th]:text-left [&_th]:px-2 [&_th]:py-1.5 [&_th]:bg-white/[0.04] [&_th]:border [&_th]:border-white/[0.08] [&_th]:text-white/80 [&_th]:font-semibold
                    [&_td]:px-2 [&_td]:py-1.5 [&_td]:border [&_td]:border-white/[0.06] [&_td]:text-white/80
                    [&_tr:nth-child(even)]:bg-white/[0.02]
                  "
                  >
                    <ReactMarkdown>{recommendations}</ReactMarkdown>
                  </div>
                )}
              </div>
            )}

            {/* Global feedback bar (admin only) — always visible */}
            {isAdmin && expanded && recommendations && (
              <GlobalFeedback
                variant={variant}
                recommendations={recommendations}
                feedbackMutation={feedbackMutation}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// --- Global feedback component (always shows at bottom of analysis) ---
function GlobalFeedback({
  variant,
  recommendations,
  feedbackMutation,
}: {
  variant: string;
  recommendations: string;
  feedbackMutation: FeedbackMutation;
}) {
  const [mode, setMode] = useState<"idle" | "positive" | "correction">("idle");
  const [sent, setSent] = useState<"up" | "down" | null>(null);
  const [positiveNote, setPositiveNote] = useState("");
  const [correctionText, setCorrectionText] = useState("");

  const { t } = useTranslation("recommendationsPanel");

  const handleSubmit = (rating: number) => {
    feedbackMutation.mutate({
      rating,
      recommendationSnippet: recommendations.slice(0, 500),
      userCorrection: rating === 1 ? positiveNote || undefined : correctionText || undefined,
      variant,
    });
    setSent(rating === 1 ? "up" : "down");
    setMode("idle");
    setPositiveNote("");
    setCorrectionText("");
    setTimeout(() => setSent(null), 3000);
  };

  return (
    <div className="px-4 py-3 border-t border-white/[0.06] bg-white/[0.02]">
      {sent && (
        <div className="flex items-center gap-2 text-xs text-emerald-400">
          <CheckCircle className="w-3.5 h-3.5" />
          <span>
            {sent === "up" ? t("globalFeedback.savedPositive") : t("globalFeedback.savedNegative")}
          </span>
        </div>
      )}

      {!sent && mode === "idle" && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/60">{t("globalFeedback.rate")}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMode("positive")}
              disabled={feedbackMutation.isPending}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs text-emerald-400 hover:bg-emerald-900/30 border border-emerald-700/30 hover:border-emerald-600/50 transition-colors"
            >
              <ThumbsUp className="w-3.5 h-3.5" />
              <span>{t("globalFeedback.good")}</span>
            </button>
            <button
              onClick={() => setMode("correction")}
              disabled={feedbackMutation.isPending}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs text-red-400 hover:bg-red-900/30 border border-red-700/30 hover:border-red-600/50 transition-colors"
            >
              <ThumbsDown className="w-3.5 h-3.5" />
              <span>{t("globalFeedback.poor")}</span>
            </button>
          </div>
        </div>
      )}

      {!sent && mode === "positive" && (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-white/60 block mb-1">
              {t("globalFeedback.positiveLabel")}
            </label>
            <textarea
              value={positiveNote}
              onChange={(e) => setPositiveNote(e.target.value)}
              placeholder='np. "Dobrze zidentyfikował wąskie gardło na etapie show rate"'
              className="w-full bg-[#0a0c10]/60 border border-white/[0.08] rounded px-3 py-2 text-xs text-white/80 placeholder:text-white/40 resize-none focus:outline-none focus:border-emerald-600/50"
              rows={2}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => {
                setMode("idle");
                setPositiveNote("");
              }}
              className="px-3 py-1.5 rounded text-xs text-white/60 hover:text-white/70 transition-colors"
            >
              {t("globalFeedback.cancel")}
            </button>
            <button
              onClick={() => handleSubmit(1)}
              disabled={feedbackMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60 border border-emerald-700/40 transition-colors"
            >
              {feedbackMutation.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  {t("globalFeedback.send")} <ThumbsUp className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {!sent && mode === "correction" && (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-white/60 block mb-1">
              {t("globalFeedback.negativeLabel")}
            </label>
            <textarea
              value={correctionText}
              onChange={(e) => setCorrectionText(e.target.value)}
              placeholder='np. "CPL jest ok ale nie zauważył problemu z show rate"'
              className="w-full bg-[#0a0c10]/60 border border-white/[0.08] rounded px-3 py-2 text-xs text-white/80 placeholder:text-white/40 resize-none focus:outline-none focus:border-red-600/50"
              rows={2}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => {
                setMode("idle");
                setCorrectionText("");
              }}
              className="px-3 py-1.5 rounded text-xs text-white/60 hover:text-white/70 transition-colors"
            >
              {t("globalFeedback.cancel")}
            </button>
            <button
              onClick={() => handleSubmit(0)}
              disabled={feedbackMutation.isPending || !correctionText.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-red-900/40 text-red-300 hover:bg-red-900/60 border border-red-700/40 transition-colors disabled:opacity-50"
            >
              {feedbackMutation.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  {t("globalFeedback.send")} <ThumbsDown className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
